<template>
  <div>
    <b-card v-if="deleting" class="mt-3" border-variant="danger">
      <b-progress :max="total" height="2rem" class="mb-2">
        <b-progress-bar :value="done" variant="danger">
          <span v-translate="{count: done, total: total}">Deleting: <strong>%{count} / %{total}</strong></span>
        </b-progress-bar>
      </b-progress>
      <b-row>
        <b-col md="8">
          <small class="text-muted">
            <span v-for="(phase, index) in phases" :key="phase.key">
              <span v-if="index > 0"> &middot; </span>{{ phase.label }}: {{ phase.count }}
            </span>
          </small>
        </b-col>
        <b-col md="4" class="text-md-right">
          <small class="text-muted">
            <span v-translate="{rate: rateLabel}">%{rate} rows/s</span> &middot;
            <span v-translate="{eta: etaLabel}">ETA %{eta}</span>
          </small>
        </b-col>
      </b-row>
      <p class="mb-0 mt-1"><small class="text-muted" v-translate>The results table was hidden to speed up the delete — it will reload with fresh data when the run ends.</small></p>
      <b-button size="sm" variant="outline-secondary" class="mt-2" @click="$emit('cancel')" :disabled="cancelled">
        <translate key="cancel-delete">Cancel</translate>
      </b-button>
      <small class="text-muted ml-2" v-if="cancelled" v-translate>Cancelling — letting the rows already in flight finish so none is left half-deleted</small>
    </b-card>
    <b-alert variant="success" :show="!!summary && summary.failed === 0" class="mt-3" dismissible @dismissed="$emit('dismiss')">
      <span v-if="summary" v-translate="{deleted: summary.deleted}">Deleted %{deleted} rows.</span>
      <span v-if="summary && summary.cancelled" v-translate="{skipped: summary.skipped}">Cancelled — %{skipped} rows were not started.</span>
    </b-alert>
    <b-alert variant="danger" :show="!!summary && summary.failed > 0" class="mt-3">
      <h5 v-if="summary && orphanWarning" key="delete-failed-orphans" v-translate="{deleted: summary.deleted, failed: summary.failed}">Deleted %{deleted}, failed %{failed}. The failed rows stopped at the step below and may have left orphan children:</h5>
      <h5 v-else-if="summary" key="delete-failed-plain" v-translate="{deleted: summary.deleted, failed: summary.failed}">Deleted %{deleted}, failed %{failed}. The failed rows stopped at the step below:</h5>
      <p v-if="summary && summary.cancelled" v-translate="{skipped: summary.skipped}">Cancelled — %{skipped} rows were not started.</p>
      <ul>
        <li v-for="(item, index) in errors" :key="index">
          <span v-translate="{id: item.rowId, name: item.rowLabel, step: item.step, message: item.message}">#%{id} %{name} — failed at '%{step}': %{message}</span>
        </li>
      </ul>
      <b-button size="sm" variant="warning" @click="$emit('retry')" :disabled="deleting">
        <translate key="retry-failed-deletes">Retry failed</translate>
      </b-button>
      <b-button size="sm" variant="secondary" class="ml-2" @click="$emit('export')">
        <translate key="export-failed-deletes">Export failed to CSV</translate>
      </b-button>
    </b-alert>
  </div>
</template>
<script>
  // Progress card + summary/error panel for the bulk delete engine
  // (src/mixins/bulkDeletePool.mixin.js). Purely presentational: it renders the
  // state the mixin exposes and emits back what the operator pressed, so the
  // three bulk listings share one markup instead of three copies of it.
  export default {
    name: 'BulkDeleteProgress',
    props: {
      deleting: {
        type: Boolean,
        default: false
      },
      total: {
        type: Number,
        default: 0
      },
      done: {
        type: Number,
        default: 0
      },
      // [{key, label, count}] — labels come already translated from the consumer
      phases: {
        type: Array,
        default: () => { return [] }
      },
      rateLabel: {
        type: String,
        default: '—'
      },
      etaLabel: {
        type: String,
        default: '—'
      },
      cancelled: {
        type: Boolean,
        default: false
      },
      summary: {
        type: Object,
        default: null
      },
      errors: {
        type: Array,
        default: () => { return [] }
      },
      // only the multi-step chains (client quotes) can leave orphan children
      // behind when a row fails halfway through
      orphanWarning: {
        type: Boolean,
        default: false
      }
    }
  }
</script>
